import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Alert, 
  LinearProgress, 
  Button,
  IconButton,
  Pagination,
  MenuItem,
  Select,
  FormControl,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import DownloadIcon from '@mui/icons-material/Download';
import DescriptionIcon from '@mui/icons-material/Description';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import companyService from '../../services/CompanyService';

/**
 * FileUploadApplications Component
 * 
 * A reusable component for uploading application data via CSV files.
 * 
 * Features:
 * - CSV file upload with drag and drop support
 * - Validation of required columns (COMPANY_ID, APP_NAME, APP_TYPE)
 * - Data preview with pagination
 * - Error handling and validation messages
 * - Success confirmation when data is ready to upload
 * 
 * Usage:
 * <FileUploadApplications onApplicationsDataChange={handleDataChange} />
 * 
 * Props:
 * - onApplicationsDataChange: Function to be called when valid CSV data is parsed.
 *   This function receives the parsed data as an array of application objects.
 */

const FileUploadApplications = ({ onApplicationsDataChange }) => {
  const [applications_data, setApplicationsData] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [fileName, setFileName] = useState('');
  const [validationError, setValidationError] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [fileProcessed, setFileProcessed] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [isValidatingCompanies, setIsValidatingCompanies] = useState(false);
  
  // Pagination state
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [copySuccess, setCopySuccess] = useState(false);

  // Required columns for Application upload
  const requiredColumns = ['COMPANY_ID', 'APP_NAME', 'APP_TYPE'];

  // Reset file processed flag when component mounts
  useEffect(() => {
    setFileProcessed(false);
    // Fetch the list of companies for validation
    fetchCompanies();
  }, []);

  // Fetch companies for validation
  const fetchCompanies = async () => {
    try {
      const response = await companyService.fetchCompanies();
      setCompanies(response);
    } catch (error) {
      console.error('Error fetching companies for validation:', error);
    }
  };

  // Validate if company exists
  const validateCompanies = (data) => {
    if (!companies || companies.length === 0) {
      // If we couldn't fetch companies, we'll skip this validation
      return { isValid: true, invalidCompanies: [] };
    }

    const invalidCompanies = [];
    const companyIds = companies.map(company => company.COMPANY_ID);
    const companyNames = companies.map(company => company.COMPANY_NAME);

    data.forEach((row, index) => {
      const companyIdentifier = row.COMPANY_ID;
      if (companyIdentifier && 
          !companyIds.includes(companyIdentifier) && 
          !companyNames.includes(companyIdentifier)) {
        invalidCompanies.push({
          index: index + 1, // +1 for human-readable row number (not 0-indexed)
          value: companyIdentifier
        });
      }
    });

    return {
      isValid: invalidCompanies.length === 0,
      invalidCompanies
    };
  };

  // Copy to clipboard function
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(
      () => {
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      },
      (err) => {
        console.error('Could not copy text: ', err);
      }
    );
  };

  // Handle drag over event
  const handleDragOver = (e) => {
    e.preventDefault();
    setDragging(true);
  };

  // Handle drag leave event
  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragging(false);
  };

  // Handle file drop event
  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    handleFile(file);
  };

  // Handle file input change event
  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    handleFile(file);
  };

  // Clear file and reset state
  const handleClearFile = () => {
    setFileName('');
    setApplicationsData([]);
    onApplicationsDataChange([]);
    setValidationError('');
    setFileProcessed(false);
    setPage(1);
  };

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  // Handle file upload logic
  const handleFile = (file) => {
    // Reset validation state
    setValidationError('');
    setIsValidating(true);
    setPage(1);
    
    if (!file) {
      setIsValidating(false);
      return;
    }
    
    const fileType = file.type;

    if (fileType === 'text/csv') {
      setFileName(file.name); // Display file name
      parseCSV(file);
    } else {
      setValidationError('File is not supported. Make sure to upload a CSV file.');
      setIsValidating(false);
    }
  };

  // CSV Parsing function (handling quotes and commas)
  const parseCSV = (file) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const content = e.target.result;
        const rows = content.split('\n');
        
        // Look for the header row (which should contain required column names)
        const headerRowIndex = rows.findIndex(row => 
          requiredColumns.every(col => row.includes(col))
        );
        
        if (headerRowIndex === -1 || rows.length < headerRowIndex + 2) {
          setValidationError('CSV file is not properly formatted or is missing required column headers.');
          setApplicationsData([]);
          onApplicationsDataChange([]);
          setIsValidating(false);
          setFileProcessed(false);
          return;
        }

        // Parse header row
        const header = parseCSVRow(rows[headerRowIndex]);
        
        // Validate required columns
        const missingColumns = requiredColumns.filter(col => !header.includes(col));
        if (missingColumns.length > 0) {
          setValidationError(`Missing required columns: ${missingColumns.join(', ')}`);
          setApplicationsData([]);
          onApplicationsDataChange([]);
          setIsValidating(false);
          setFileProcessed(false);
          return;
        }

        // Parse the rest of the rows
        const data = rows.slice(headerRowIndex + 1)
          .filter(row => row.trim() !== '') // Filter out empty rows
          .map((row) => {
            const values = parseCSVRow(row);
            const rowData = {};
            header.forEach((col, index) => {
              rowData[col.trim()] = values[index]?.trim() || ''; // Handling missing values
            });
            return rowData;
          });

        // Validate that there is at least one row of data
        if (data.length === 0) {
          setValidationError('CSV file contains headers but no data rows');
          setApplicationsData([]);
          onApplicationsDataChange([]);
          setIsValidating(false);
          setFileProcessed(false);
          return;
        }
        
        // Convert boolean string values to actual booleans for SOX_INSCOPE
        const normalizedData = data.map(row => ({
          ...row,
          SOX_INSCOPE: row.SOX_INSCOPE?.toLowerCase() === 'true' || row.SOX_INSCOPE === true || row.SOX_INSCOPE === "Yes" || row.SOX_INSCOPE === "yes"
        }));

        // Check for duplicate APP_NAMEs within the same company in the CSV file itself
        const companyAppPairs = {};
        let hasDuplicatesInSameCompany = false;
        const duplicatePairs = [];
        
        normalizedData.forEach(row => {
          // Get the company identifier (could be COMPANY_ID or COMPANY_NAME)
          const companyIdentifier = row.COMPANY_ID || '';
          const key = `${companyIdentifier}|${row.APP_NAME}`;
          
          if (companyAppPairs[key]) {
            hasDuplicatesInSameCompany = true;
            duplicatePairs.push({
              companyId: companyIdentifier,
              appName: row.APP_NAME
            });
          } else {
            companyAppPairs[key] = true;
          }
        });
        
        if (hasDuplicatesInSameCompany) {
          const duplicateInfo = duplicatePairs.map(pair => `"${pair.appName}" for company "${pair.companyId}"`);
          setValidationError(`Duplicate application names found in the same company: ${duplicateInfo.join(', ')}. Please remove duplicates within the same company before uploading.`);
          setApplicationsData(normalizedData); // Still show the data for review
          onApplicationsDataChange([]); // Don't pass data to parent when there are errors
          setIsValidating(false);
          setFileProcessed(false); // Don't mark as processed when there are errors
          return;
        }

        // Validate company identifiers
        setIsValidatingCompanies(true);
        const companyValidation = validateCompanies(normalizedData);
        setIsValidatingCompanies(false);
        
        if (!companyValidation.isValid) {
          const invalidEntries = companyValidation.invalidCompanies;
          const invalidRows = invalidEntries.map(entry => `Row ${entry.index}: "${entry.value}"`);
          
          setValidationError(
            `Invalid company identifiers found in the following rows: ${invalidRows.join('; ')}. 
            Please ensure all company names or IDs exist in the system.`
          );
          setApplicationsData(normalizedData); // Still show the data for review
          onApplicationsDataChange([]); // Don't pass data to parent when there are errors
          setIsValidating(false);
          setFileProcessed(false); // Don't mark as processed when there are errors
          return;
        }

        // All validations passed, now we can mark as processed and notify parent
        setValidationError(''); // Clear any previous validation errors
        setApplicationsData(normalizedData);
        setIsValidating(false);
        setFileProcessed(true); // Mark file as successfully processed
        onApplicationsDataChange(normalizedData); // Pass the data to parent
      } catch (error) {
        console.error('Error parsing CSV file:', error);
        setValidationError(`Error parsing CSV file: ${error.message}`);
        setIsValidating(false);
        setFileProcessed(false);
      }
    };
    
    reader.onerror = (error) => {
      console.error('Error reading file:', error);
      setValidationError(`Error reading file: ${error}`);
      setIsValidating(false);
      setFileProcessed(false);
    };
    
    reader.readAsText(file);
  };

  // Helper function to parse a CSV row
  const parseCSVRow = (row) => {
    const values = [];
    let inQuotes = false;
    let currentValue = '';
    
    for (let i = 0; i < row.length; i++) {
      const char = row[i];

      if (char === '"' && (i === 0 || row[i - 1] !== '"')) {
        // Toggle inQuotes when encountering a single quote
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        // If not in quotes, treat comma as separator
        values.push(currentValue);
        currentValue = '';
      } else {
        // Add char to the current value
        currentValue += char;
      }
    }
    values.push(currentValue); // Push the last value
    
    // Clean up quotes from the values
    return values.map(val => {
      // Remove surrounding quotes if they exist
      if (val.startsWith('"') && val.endsWith('"')) {
        return val.substring(1, val.length - 1);
      }
      return val;
    });
  };

  // Calculate pagination values
  const pageCount = Math.ceil(applications_data.length / rowsPerPage);
  const startIndex = (page - 1) * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, applications_data.length);
  const paginatedData = applications_data.slice(startIndex, endIndex);

  return (
    <Box sx={{ padding: '16px 0px' }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" sx={{ color: '#333', fontSize: '1.1rem', fontWeight: 500 }}>
          Upload Applications Data
        </Typography>
      </Box>

      <Typography variant="body2" sx={{ mb: 1, color: '#555' }}>
        Upload your applications data CSV file. The file should contain these required columns: <strong>COMPANY_ID</strong>, <strong>APP_NAME</strong>, and <strong>APP_TYPE</strong>.
      </Typography>
      
      <Accordion 
        disableGutters 
        elevation={0}
        sx={{ 
          mb: 3, 
          border: '1px solid rgba(0,0,0,0.08)',
          borderRadius: '6px',
          '&:before': { display: 'none' },
          '& .MuiAccordionSummary-root': {
            minHeight: '48px',
            borderRadius: '6px'
          }
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{ 
            backgroundColor: 'rgba(0,0,0,0.02)',
            color: 'text.secondary'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <InfoOutlinedIcon sx={{ fontSize: '1rem', mr: 1, color: 'primary.main' }} />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Information about Required and Optional Fields
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Your CSV file must contain the following columns. Note that COMPANY_ID and APPLICATION_OWNER will be validated against existing data in the system.
          </Typography>
          <Box sx={{ mb: 2 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: 'rgba(0,0,0,0.03)' }}>
                  <th style={{ padding: '8px 16px', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600 }}>Column Name</th>
                  <th style={{ padding: '8px 16px', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600 }}>Required</th>
                  <th style={{ padding: '8px 16px', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600 }}>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ backgroundColor: '#f9fafb' }}>
                  <td style={{ padding: '6px 16px', fontSize: '0.75rem', fontWeight: 500, color: 'primary.main' }}>
                    COMPANY_ID
                  </td>
                  <td style={{ padding: '6px 16px', fontSize: '0.75rem' }}>
                    <span style={{ color: '#e53935' }}>Yes</span>
                  </td>
                  <td style={{ padding: '6px 16px', fontSize: '0.75rem', color: 'text.secondary' }}>
                    The ID or name of the company that owns the application (you can use either COMPANY_ID or COMPANY_NAME). Values will be validated against existing companies in the system.
                  </td>
                </tr>
                <tr style={{ backgroundColor: 'white' }}>
                  <td style={{ padding: '6px 16px', fontSize: '0.75rem', fontWeight: 500, color: 'primary.main' }}>
                    APP_NAME
                  </td>
                  <td style={{ padding: '6px 16px', fontSize: '0.75rem' }}>
                    <span style={{ color: '#e53935' }}>Yes</span>
                  </td>
                  <td style={{ padding: '6px 16px', fontSize: '0.75rem', color: 'text.secondary' }}>
                    The name of the application. Duplicate application names are allowed as long as they belong to different companies.
                  </td>
                </tr>
                <tr style={{ backgroundColor: '#f9fafb' }}>
                  <td style={{ padding: '6px 16px', fontSize: '0.75rem', fontWeight: 500, color: 'primary.main' }}>
                    APP_TYPE
                  </td>
                  <td style={{ padding: '6px 16px', fontSize: '0.75rem' }}>
                    <span style={{ color: '#e53935' }}>Yes</span>
                  </td>
                  <td style={{ padding: '6px 16px', fontSize: '0.75rem', color: 'text.secondary' }}>
                    The type of application (e.g., Application, Database, Tool, etc.)
                  </td>
                </tr>
                <tr style={{ backgroundColor: 'white' }}>
                  <td style={{ padding: '6px 16px', fontSize: '0.75rem', fontWeight: 500, color: 'primary.main' }}>
                    APP_DESCRIPTION
                  </td>
                  <td style={{ padding: '6px 16px', fontSize: '0.75rem' }}>
                    No
                  </td>
                  <td style={{ padding: '6px 16px', fontSize: '0.75rem', color: 'text.secondary' }}>
                    A description of the application
                  </td>
                </tr>
                <tr style={{ backgroundColor: '#f9fafb' }}>
                  <td style={{ padding: '6px 16px', fontSize: '0.75rem', fontWeight: 500, color: 'primary.main' }}>
                    SOX_INSCOPE
                  </td>
                  <td style={{ padding: '6px 16px', fontSize: '0.75rem' }}>
                    No
                  </td>
                  <td style={{ padding: '6px 16px', fontSize: '0.75rem', color: 'text.secondary' }}>
                    Whether the application is in scope for SOX compliance. Use 'true'/'false' or 'yes'/'no' (case insensitive).
                  </td>
                </tr>
                <tr style={{ backgroundColor: 'white' }}>
                  <td style={{ padding: '6px 16px', fontSize: '0.75rem', fontWeight: 500, color: 'primary.main' }}>
                    APPLICATION_OWNER
                  </td>
                  <td style={{ padding: '6px 16px', fontSize: '0.75rem' }}>
                    No
                  </td>
                  <td style={{ padding: '6px 16px', fontSize: '0.75rem', color: 'text.secondary' }}>
                    Email address of the application owner. Must be a valid user email in the system (e.g., user@example.com). For multiple owners, separate emails with commas.
                  </td>
                </tr>
              </tbody>
            </table>
          </Box>
        </AccordionDetails>
      </Accordion>
      
      {!applications_data.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            border: dragging 
              ? '2px solid primary.main' 
              : '2px dashed rgba(0, 0, 0, 0.12)',
            borderRadius: '8px',
            padding: '32px 20px',
            textAlign: 'center',
            cursor: 'pointer',
            backgroundColor: dragging 
              ? 'rgba(25, 118, 210, 0.04)' 
              : '#fafafa',
            marginBottom: '20px',
            transition: 'all 0.2s ease',
            position: 'relative',
            overflow: 'hidden'
          }}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => document.getElementById('applications-file-input').click()}
        >
          {(isValidating || isValidatingCompanies) && (
            <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0 }}>
              <LinearProgress sx={{ height: 4 }} />
            </Box>
          )}
          
          <input
            type="file"
            accept=".csv"
            onChange={handleFileInputChange}
            style={{ display: 'none' }}
            id="applications-file-input"
          />
          
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1.5, opacity: 0.7 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 0.5, color: '#333' }}>
              {isValidatingCompanies ? 'Validating companies...' : 'Drag & drop your file here'}
            </Typography>
            <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
              or
            </Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<DescriptionIcon />}
              onClick={() => document.getElementById('applications-file-input').click()}
              sx={{ 
                textTransform: 'none', 
                boxShadow: 'none',
                borderRadius: '6px',
                px: 2,
                '&:hover': {
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }
              }}
            >
              Browse Files
            </Button>
            <Typography variant="caption" sx={{ mt: 2, color: '#777' }}>
              Supported format: CSV file
            </Typography>
          </Box>
        </Paper>
      )}

      {/* File name display */}
      {fileName && !validationError && (
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          backgroundColor: 'rgba(25, 118, 210, 0.08)', 
          p: 1.5, 
          pl: 2,
          borderRadius: '6px', 
          mb: 2
        }}>
          <DescriptionIcon sx={{ color: 'primary.main', mr: 1.5, fontSize: '1.25rem' }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, color: '#333' }}>
              {fileName}
            </Typography>
            <Typography variant="caption" sx={{ color: '#666' }}>
              {applications_data.length} rows • CSV file 
            </Typography>
          </Box>
          <IconButton size="small" onClick={handleClearFile}>
            <HighlightOffIcon sx={{ fontSize: '1.25rem', color: '#777' }} />
          </IconButton>
        </Box>
      )}

      {/* Validation Error message */}
      {validationError && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 2, 
            '& .MuiAlert-message': { fontWeight: 400 } 
          }}
        >
          {validationError}
        </Alert>
      )}

      {/* Data preview */}
      {applications_data.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Typography variant="subtitle2" sx={{ color: '#333', fontWeight: 500 }}>
              Data Preview
            </Typography>
            <Typography variant="caption" sx={{ color: '#666' }}>
              Showing {startIndex + 1}-{endIndex} of {applications_data.length} rows
            </Typography>
          </Box>
          
          <Box sx={{ 
            border: '1px solid rgba(224, 224, 224, 1)', 
            borderRadius: '6px', 
            overflow: 'hidden',
            mb: 2
          }}>
            <Box sx={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: 'rgba(25, 118, 210, 0.04)' }}>
                    {Object.keys(applications_data[0]).map((header, index) => (
                      <th
                        key={index}
                        style={{
                          padding: '10px 16px',
                          textAlign: 'left',
                          fontSize: '0.75rem',
                          color: '#333',
                          fontWeight: 600,
                          borderBottom: '1px solid rgba(224, 224, 224, 1)',
                          whiteSpace: 'nowrap',
                          backgroundColor: requiredColumns.includes(header) ? 'rgba(25, 118, 210, 0.08)' : 'rgba(25, 118, 210, 0.04)'
                        }}
                      >
                        {header}
                        {requiredColumns.includes(header) && (
                          <span style={{ color: 'primary.main', marginLeft: '2px' }}>*</span>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {paginatedData.map((row, rowIndex) => (
                    <tr key={rowIndex} style={{ backgroundColor: rowIndex % 2 === 0 ? 'white' : '#f9fafb' }}>
                      {Object.entries(row).map(([key, value], index) => (
                        <td
                          key={index}
                          style={{
                            fontSize: '0.75rem',
                            padding: '8px 16px',
                            borderBottom: rowIndex < paginatedData.length - 1 ? '1px solid rgba(224, 224, 224, 1)' : 'none',
                            color: '#333',
                            maxWidth: '200px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {typeof value === 'boolean' ? value.toString() : (value || '-')}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          </Box>
          
          {/* Pagination controls */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            mb: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ mr: 1, color: '#666' }}>
                Rows per page:
              </Typography>
              <FormControl variant="standard" size="small">
                <Select
                  value={rowsPerPage}
                  onChange={handleChangeRowsPerPage}
                  sx={{ 
                    fontSize: '0.875rem',
                    '& .MuiSelect-select': { 
                      py: 0.5, 
                      minWidth: '60px' 
                    }
                  }}
                >
                  <MenuItem value={5}>5</MenuItem>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                </Select>
              </FormControl>
            </Box>
            
            <Pagination
              count={pageCount}
              page={page}
              onChange={handleChangePage}
              size="small"
              color="primary"
              showFirstButton
              showLastButton
              siblingCount={1}
              boundaryCount={1}
              sx={{
                '& .MuiPaginationItem-root': {
                  fontSize: '0.875rem',
                  minWidth: '28px',
                  height: '28px',
                  borderRadius: '4px',
                },
                '& .Mui-selected': {
                  backgroundColor: 'rgba(25, 118, 210, 0.12)',
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.2)',
                  }
                }
              }}
            />
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleClearFile}
              sx={{ 
                textTransform: 'none',
                borderColor: 'rgba(0,0,0,0.12)',
                color: 'text.secondary',
                borderRadius: '6px',
                '&:hover': {
                  borderColor: 'text.primary',
                  backgroundColor: 'rgba(0,0,0,0.04)'
                }
              }}
            >
              Clear
            </Button>
          </Box>
        </Box>
      )}

      {/* Message area - shows either validation error or success message */}
      {applications_data.length > 0 && (
        validationError ? (
          /* Error message */
          <Alert 
            severity="error" 
            sx={{ 
              mt: 3,
              mb: 2,
              '& .MuiAlert-message': { 
                fontWeight: 400,
                display: 'flex',
                alignItems: 'center'
              } 
            }}
          >
            <Typography variant="body2">
              {validationError}
            </Typography>
          </Alert>
        ) : (
          /* Success message - only show when file is processed */
          fileProcessed && (
            <Alert 
              severity="success" 
              icon={<CloudDoneIcon />}
              sx={{ 
                mt: 3,
                mb: 2,
                '& .MuiAlert-message': { 
                  fontWeight: 400,
                  display: 'flex',
                  alignItems: 'center'
                } 
              }}
            >
              <Typography variant="body2">
                <strong>{fileName}</strong> with <strong>{applications_data.length} rows</strong> has been validated and is ready to upload. All company identifiers were found in the system and there are no duplicate application names within the same company. Click "Upload" to proceed.
              </Typography>
            </Alert>
          )
        )
      )}
    </Box>
  );
};

export default FileUploadApplications; 